<template>
  <section :class="compact ? 'bg-transparent' : 'border-t border-default/60 bg-default'">
    <UContainer :class="compact ? 'p-0' : 'py-12 sm:py-14'">
      <div class="mx-auto max-w-md space-y-4">
        <h2
          v-if="!compact"
          class="text-center text-xl font-semibold"
        >
          {{ title || item?.title || 'آزمون' }}
        </h2>
        <p
          v-if="subtitle && !compact"
          class="text-center text-sm text-muted"
        >
          {{ subtitle }}
        </p>
        <div
          v-if="!quizId || builderPreview"
          class="rounded-2xl border border-dashed p-6 text-center text-sm text-muted"
        >
          آزمون پزشکی
        </div>
        <div
          v-else-if="loadingQuiz"
          class="rounded-2xl border border-default/60 p-5 text-center text-sm text-muted"
          role="status"
        >
          در حال آماده‌سازی آزمون…
        </div>
        <div
          v-else-if="quizLoadError"
          class="space-y-3 rounded-2xl border border-error/30 p-5 text-center"
          role="alert"
        >
          <p class="text-sm text-error">
            {{ quizLoadError }}
          </p>
          <UButton
            size="sm"
            variant="soft"
            :loading="loadingQuiz"
            @click="loadQuiz"
          >
            تلاش دوباره
          </UButton>
        </div>
        <div
          v-else-if="!questions.length"
          class="rounded-2xl border border-dashed p-5 text-center text-sm text-muted"
        >
          این آزمون هنوز سؤالی ندارد.
        </div>
        <div
          v-else-if="completed && result"
          class="space-y-3 rounded-2xl border border-primary/20 bg-primary/5 p-5 text-center"
          role="status"
          aria-live="polite"
        >
          <UIcon
            name="i-lucide-clipboard-check"
            class="mx-auto size-8 text-primary"
          />
          <p class="text-lg font-semibold">
            {{ result.title }}
          </p>
          <p
            v-if="result.description"
            class="text-sm leading-6 text-muted"
          >
            {{ result.description }}
          </p>
          <p class="text-sm font-medium">
            امتیاز شما: {{ toFaDigits(result.score) }}
          </p>
          <p class="text-xs leading-5 text-muted">
            این آزمون فقط راهنمای اولیه است و جایگزین ارزیابی و تشخیص پزشک نیست.
            {{ result.sentToClinic ? 'نتیجه برای بررسی در اختیار پزشک قرار گرفت.' : 'پاسخ‌های شما ثبت شد.' }}
          </p>
        </div>
        <LeadFlowShell
          v-else
          :labels="labels"
          :step="step"
          :loading="saving"
          :next-disabled="nextDisabled"
          :next-label="step === labels.length - 1 ? 'مشاهده نتیجه' : 'سؤال بعد'"
          @next="next"
          @back="step = Math.max(0, step - 1)"
        >
          <div
            v-if="isPhoneStep"
            class="space-y-3"
          >
            <AuthPhoneInput v-model="phone" />
            <p class="text-xs leading-5 text-muted">
              شمارهٔ همراه برای ثبت نتیجه و پیگیری احتمالی در اختیار پزشک/کلینیک قرار می‌گیرد.
            </p>
          </div>
          <LeadQuestionFields
            v-else-if="current"
            v-model="answers"
            :questions="[current]"
          />
        </LeadFlowShell>
        <p
          v-if="error"
          class="text-center text-sm text-error"
          role="alert"
        >
          {{ error }}
        </p>
      </div>
    </UContainer>
  </section>
</template>

<script setup lang="ts">
import AuthPhoneInput from '~/components/auth/PhoneInput.vue'
import type { LeadQuestion } from '~/utils/leads/questions'
import { isQuestionAnswered } from '~/utils/leads/questions'
import { isCompleteIrMobile } from '~/utils/phone'
import { apiErrorMessage } from '~/utils/apiError'
import { toFaDigits } from '~/utils/faDigits'

const props = defineProps<{
  title?: string
  subtitle?: string
  quizId?: string
  builderPreview?: boolean
  compact?: boolean
}>()

type QuizItem = { title: string; questions: LeadQuestion[]; gateType: string }
type QuizResult = { title: string; description: string; score: number; sentToClinic: boolean }

const item = ref<QuizItem | null>(null)
const answers = ref<Record<string, unknown>>({})
const phone = ref('')
const step = ref(0)
const saving = ref(false)
const error = ref('')
const result = ref<QuizResult | null>(null)
const loadingQuiz = ref(Boolean(props.quizId && !props.builderPreview))
const quizLoadError = ref('')
const completed = ref(false)

onMounted(() => {
  if (props.quizId && !props.builderPreview) void loadQuiz()
})

async function loadQuiz() {
  if (!props.quizId) return
  loadingQuiz.value = true
  quizLoadError.value = ''
  try {
    const response = await $fetch<{ item: QuizItem }>(`/api/leads/quizzes/${props.quizId}`)
    item.value = response.item
  }
  catch (err) {
    quizLoadError.value = apiErrorMessage(err, 'بارگذاری آزمون ناموفق بود')
  }
  finally {
    loadingQuiz.value = false
  }
}

const questions = computed(() => item.value?.questions ?? [])
const gateBefore = computed(() => item.value?.gateType === 'before')
const gateAfter = computed(() => item.value?.gateType === 'after')
const phoneStepIndex = computed(() => questions.value.length + (gateBefore.value ? 1 : 0))
const labels = computed(() => [
  ...(gateBefore.value ? ['موبایل'] : []),
  ...questions.value.map((_, i) => `سؤال ${i + 1}`),
  ...(gateAfter.value ? ['موبایل'] : []),
])
const isPhoneStep = computed(() => (gateBefore.value && step.value === 0) || (gateAfter.value && step.value === phoneStepIndex.value))
const questionIndex = computed(() => step.value - (gateBefore.value ? 1 : 0))
const current = computed(() => (!isPhoneStep.value ? questions.value[questionIndex.value] : undefined))
const nextDisabled = computed(() => {
  if (isPhoneStep.value) return !isCompleteIrMobile(phone.value)
  const q = current.value
  return Boolean(q && q.required !== false && !isQuestionAnswered(q, answers.value[q.id]))
})

async function next() {
  const last = labels.value.length - 1
  if (step.value < last) {
    step.value += 1
    return
  }
  if (!props.quizId) return
  saving.value = true
  error.value = ''
  try {
    const res = await $fetch<{ item: { result: { title: string; description: string }; score: number } }>(`/api/leads/quizzes/${props.quizId}/submit`, {
      method: 'POST',
      body: { answers: answers.value, phone: phone.value || undefined },
    })
    result.value = {
      title: res.item.result.title || 'آزمون تکمیل شد',
      description: res.item.result.description || '',
      score: res.item.score,
      sentToClinic: gateBefore.value || gateAfter.value || Boolean(phone.value),
    }
    completed.value = true
  }
  catch (err) {
    error.value = apiErrorMessage(err, 'ارسال آزمون ناموفق بود')
  }
  finally {
    saving.value = false
  }
}
</script>
